(() => {
  const initialize = (hub) => {
    if (!hub || hub.dataset.initialized === "true") return;
    hub.dataset.initialized = "true";

    const input = hub.querySelector("[data-sw-device-hub-search]");
    const clear = hub.querySelector("[data-sw-device-hub-clear]");
    const submit = hub.querySelector("[data-sw-device-hub-submit]");
    const filter = hub.querySelector("[data-sw-device-hub-filter]");
    const cards = Array.from(
      hub.querySelectorAll(
        "[data-sw-device-hub-grid] [data-sw-device-hub-card]",
      ),
    );
    const empty = hub.querySelector("[data-sw-device-hub-empty]");
    const status = hub.querySelector("[data-sw-device-hub-status]");
    const grid = hub.querySelector("[data-sw-device-hub-grid]");
    const pagination = hub.querySelector("[data-sw-device-hub-pagination]");
    const retry = hub.querySelector("[data-sw-device-hub-retry]");
    if (!grid || (!pagination && ((!input && !filter) || !cards.length))) return;
    let loading = false;
    let incomplete = Boolean(pagination);

    const render = () => {
      const query = input?.value.trim().toLowerCase() || "";
      const queryTerms = query.split(/\s+/).filter(Boolean);
      const selectedFilter = filter?.value || "all";
      let visible = 0;
      cards.forEach((card) => {
        const searchText = card.dataset.search || "";
        const matchesSearch =
          !queryTerms.length ||
          queryTerms.every((term) => searchText.includes(term));
        const matchesFilter =
          selectedFilter === "all" ||
          card.dataset.family === selectedFilter;
        const matches = matchesSearch && matchesFilter;
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      if (clear) clear.hidden = !query;
      if (empty) empty.hidden = visible !== 0 || incomplete;
      if (status)
        status.textContent = `${visible} ${visible === 1 ? "model" : "models"}${
          loading ? " · loading all models…" : incomplete
            ? " loaded · use the model-page links or retry to see the rest."
            : ""
        }`;
    };

    // Use the same Liquid-filtered collection records, not a second catalog.
    // Request just this section; preserve locale, preview theme and view params.
    const loadAllPages = async () => {
      if (!pagination || loading) return;
      loading = true;
      incomplete = true;
      pagination.hidden = true;
      if (retry) retry.hidden = true;
      grid.setAttribute("aria-busy", "true");
      render();
      const pageCount = Number(hub.dataset.catalogPages);
      const visited = new Set([Number(hub.dataset.catalogPage)]);
      const queue = Array.from(pagination.querySelectorAll("[data-sw-device-hub-page]"), (link) => link.href);
      const knownCards = new Set(cards.map((card) => card.getAttribute("href")));
      try {
        while (queue.length) {
          const url = new URL(queue.shift(), window.location.href);
          const currentURL = new URL(window.location.href);
          const page = Number(url.searchParams.get("page") || 1);
          if (url.origin !== currentURL.origin || url.pathname !== currentURL.pathname ||
              !Number.isInteger(page) || page < 1 || page > pageCount) {
            throw new Error("Invalid model-page URL");
          }
          if (visited.has(page)) continue;
          for (const key of ["preview_theme_id", "view"]) {
            if (currentURL.searchParams.has(key)) url.searchParams.set(key, currentURL.searchParams.get(key));
          }
          url.searchParams.set("section_id", hub.dataset.sectionId);
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 15000);
          let pageHub;
          try {
            const response = await fetch(url.href, { credentials: "same-origin", signal: controller.signal });
            if (!response.ok) throw new Error("Model page could not be loaded");
            const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
            pageHub = parsed.querySelector("[data-sw-device-hub]");
          } finally {
            clearTimeout(timeout);
          }
          if (!pageHub || pageHub.id !== hub.id || Number(pageHub.dataset.catalogPage) !== page ||
              !pageHub.querySelector("[data-sw-device-hub-grid]")) {
            throw new Error("Incomplete model-page response");
          }
          visited.add(page);
          pageHub.querySelectorAll("[data-sw-device-hub-page]").forEach((link) => queue.push(link.getAttribute("href")));
          pageHub.querySelectorAll("[data-sw-device-hub-grid] [data-sw-device-hub-card]").forEach((card) => {
            const href = card.getAttribute("href");
            if (!href || knownCards.has(href)) return;
            knownCards.add(href);
            card.querySelectorAll("img").forEach((img) => img.setAttribute("loading", "lazy"));
            cards.push(card);
          });
          // Match Liquid's descending release-year/handle order across batches.
          cards.sort((a, b) => a.dataset.sortKey === b.dataset.sortKey ? 0 : a.dataset.sortKey > b.dataset.sortKey ? -1 : 1);
          cards.forEach((card, index) => {
            card.querySelector('meta[itemprop="position"]')?.setAttribute("content", String(index + 1));
            grid.appendChild(card);
          });
          render();
        }
        if (visited.size !== pageCount) throw new Error("Not all model pages were received");
        incomplete = false;
      } catch {
        // Never silently present a partial catalog as complete. The original
        // server-rendered previous/next links also work without JavaScript.
        pagination.hidden = false;
        if (retry) retry.hidden = false;
      } finally {
        loading = false;
        grid.setAttribute("aria-busy", "false");
        render();
      }
    };

    input?.addEventListener("input", render);
    input?.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        render();
      }
      if (event.key === "Escape") {
        input.value = "";
        render();
        input.blur();
      }
    });
    clear?.addEventListener("click", () => {
      input.value = "";
      render();
      input.focus();
    });
    submit?.addEventListener("click", () => {
      input.focus();
      render();
    });
    filter?.addEventListener("change", render);
    retry?.addEventListener("click", loadAllPages);
    render();
    if (pagination) loadAllPages();
  };

  const initializeAll = (root = document) =>
    root.querySelectorAll("[data-sw-device-hub]").forEach(initialize);
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => initializeAll(), {
      once: true,
    });
  else initializeAll();
  document.addEventListener("shopify:section:load", (event) =>
    initializeAll(event.target),
  );
})();
